import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { obterIdTenent } from '../../../services/seguranca';
import { useAppSelector } from '../../../store/hooks';
import ModalForm from '../../../components/modal-form';
import { TipoFormulario } from '../../../services/constantes';
import { Campo, Opcao } from '../../../services/tipos';
import { NFServiceService } from '../../../services/NFServiceService';

interface ModalNovaNotaProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
}

const ModalNovaNota: React.FC<ModalNovaNotaProps> = ({ show, onHide, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [locatarios, setLocatarios] = useState<Opcao[]>([]);
    const [servicos, setServicos] = useState<Opcao[]>([]);
    const token = useAppSelector((state) => state.token);
    const [tipoEmissao, setTipoEmissao] = useState('agora');

    useEffect(() => {
        if (show) {
            carregarDados();
        }
    }, [show]);

    const carregarDados = async () => {
        try {
            const [locatariosRes, servicosRes] = await Promise.all([
                api.get(`/LocatariosPorEmpresa/${obterIdTenent(token)}`),
                api.get(`/ServicoNFSePorEmpresa/${obterIdTenent(token)}`)
            ]);

            setLocatarios(locatariosRes.data.map((l: any) => ({
                label: l.nome,
                valor: l.id
            })));

            setServicos(servicosRes.data.map((s: any) => ({
                label: `${s.codigo} - ${s.descricao}`,
                valor: s.id
            })));

        } catch (error) {
            console.error("Erro ao carregar dados", error);
            toast.error("Erro ao carregar dados para cadastro.");
        }
    };

    const campos: Campo[] = [
        {
            linha: 1,
            coluna: 1,
            largura: 12,
            nome: "tipoEmissao",
            label: "Quando Emitir?",
            tipo: "toggle",
            required: true,
            opcoes: [
                { label: "Emitir Agora", valor: "agora", color: "#28a745" },
                { label: "Agendar", valor: "agendar", color: "#007bff" }
            ],
            defaultValueToggle: "agora",
            onCampoDigitar: (val) => setTipoEmissao(val)
        },
        {
            linha: 2,
            coluna: 1,
            largura: 6,
            nome: "dataHoraEmissao",
            label: "Data da Emissão",
            tipo: "date",
            required: tipoEmissao === 'agendar',
            visible: (dados) => dados.tipoEmissao === 'agendar'
        },
        {
            linha: 3,
            coluna: 1,
            largura: 6,
            nome: "valor",
            label: "Valor (R$)",
            tipo: "money",
            required: true
        },
        {
            linha: 3,
            coluna: 2,
            largura: 6,
            nome: "idLocatario",
            label: "Locatário",
            tipo: "select",
            required: true,
            opcoes: locatarios,
            selectBoxIsCleareable: true
        },
        {
            linha: 4,
            coluna: 1,
            largura: 12,
            nome: "idServico",
            label: "Serviço",
            tipo: "select",
            required: true,
            opcoes: servicos
        },
        {
            linha: 5,
            coluna: 1,
            largura: 12,
            nome: "descricao",
            label: "Descrição do Pagamento",
            tipo: "memo",
            required: false
        },
    ];

    const handleSalvar = async (dados: any) => {
        setLoading(true);
        try {
            if (dados.tipoEmissao === 'agora') {
                await NFServiceService.emitir(
                    undefined, // idPagamento null
                    dados.idServico,
                    obterIdTenent(token),
                    dados.idLocatario, // idLocatario
                    dados.valor,
                    dados.descricao
                );
                toast.success("Nota emitida com sucesso!");
            } else {
                // Call Schedule Endpoint
                await NFServiceService.agendar(
                    null,
                    dados.idLocatario,
                    dados.idServico,
                    obterIdTenent(token),
                    dados.dataHoraEmissao,
                    dados.valor,
                    dados.descricao
                );
                toast.success("Emissão agendada com sucesso!");
            }

            onSuccess();
            onHide();
        } catch (error: any) {
            console.error(error);
            toast.error("Erro ao processar: " + (error.response?.data?.Mensagem || error.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <ModalForm
            show={show}
            modo={TipoFormulario.tpCadastro}
            tamanho="lg"
            campos={campos}
            carregando={loading}
            onHide={onHide}
            onCancel={onHide}
            onSave={handleSalvar}
            titulo="Nova Nota Fiscal"
            apiConfig={{
                endpoint: "",
                requisicaoBemSucedida: () => { },
                tratarErro: () => { },
                idTenentCampo: 'idEmpresa'
            }}
        />
    );
};

export default ModalNovaNota;
